import { createContext, Dispatch, ReactNode, useEffect, useReducer } from 'react';
import { StudentGoalBase as OriginalStudentGoalBase } from 'src/pages/single-student/singleStudent.types';

import { RecordSessionFull, ScheduleEvent } from '../schedule.types';

export interface StudentGoalBase extends OriginalStudentGoalBase {
  type: 'setss';
}

export interface SeitGoal {
  id: string;
  name: string;
  activity?: string[];
  participation?: string;
  outcome?: string;
  type: 'seit';
  progress?: number;
}

export type UninitializedForm = {
  isInitialized: false;
  formData?: never;
};

export type FormData = {
  date?: string;
  location?: string;
  finishTime?: string;
  startTime?: string;
  studentId?: string;
  note?: string;
  goals: (StudentGoalBase | SeitGoal)[];
  cancellationReason?: string;
  cancellationReasonId?: string;
  numberOfStudents?: number;
};

export type FormDataErrors = Partial<Record<keyof FormData | (string & {}), string>>; // eslint-disable-line @typescript-eslint/ban-types

export type FormOptions = { limitDatePicker?: boolean };

export type EditionForm = {
  isInitialized: true;
  parentEvent: ScheduleEvent;
  existingSession?: RecordSessionFull;
  isLoading: boolean;
  formData: FormData;
  errors?: FormDataErrors;
  options?: FormOptions;
};

export type CreationForm = {
  isInitialized: true;
  parentDate: Date;
  formData: FormData;
  errors?: FormDataErrors;
  options?: FormOptions;
};

export type FormState = UninitializedForm | EditionForm | CreationForm;

export type FormActions =
  | {
      type: 'init';
      payload: Omit<EditionForm, 'isInitialized'> | Omit<CreationForm, 'isInitialized'>;
    }
  | { type: 'loadingDone'; payload: RecordSessionFull }
  | { type: 'selectStudent'; payload?: string }
  | { type: 'clearStudent' }
  | { type: 'setDate'; payload: string }
  | { type: 'setGoalProgress'; payload: { goalId: string; progress: number } }
  | { type: 'setStartTime'; payload: string | undefined }
  | { type: 'setFinishTime'; payload: string | undefined }
  | { type: 'setLocation'; payload?: string }
  | { type: 'addNewGoal'; payload: StudentGoalBase }
  | { type: 'removeGoal'; payload: StudentGoalBase }
  | { type: 'clearGoals' }
  | { type: 'setCancellationReason'; payload: string }
  | { type: 'setNote'; payload: string }
  | { type: 'removeNote' }
  | { type: 'reset' }
  | { type: 'setErrors'; payload?: FormDataErrors }
  | { type: 'numberOfStudents'; payload: number }
  | { type: 'addSeitGoal'; payload: { id: string; name: string } }
  | { type: 'removeSeitGoal'; payload: { index: number } }
  | {
      type: 'updateSeitGoal';
      payload: { index: number; field: keyof SeitGoal; value: string | string[] };
    };

const formStateReducer = (state: FormState, action: FormActions): FormState => {
  const type = action.type;
  if (type === 'init') {
    const initialGoals = action.payload.formData?.goals || [];
    return {
      isInitialized: true,
      ...action.payload,
      formData: {
        ...action.payload.formData,
        goals: initialGoals,
      },
    } as CreationForm | EditionForm;
  }
  if (type === 'reset') {
    return { isInitialized: false } as UninitializedForm;
  }

  if (state.isInitialized && ('parentEvent' in state || 'parentDate' in state)) {
    const mutateFormData = (partial: Partial<FormData>): CreationForm | EditionForm => ({
      ...state,
      formData: { ...state.formData, ...partial },
    });
    switch (type) {
      case 'loadingDone':
        return {
          ...state,
          isLoading: false,
          existingSession: action.payload,
          formData:
            action.payload.isCancelled && !action.payload.isRescheduled
              ? {
                  studentId: action.payload.student.id,
                  goals: [],
                  date: action.payload.date,
                }
              : {
                  goals: action.payload.goals.map((g) => {
                    if (action.payload.student.serviceType === 'SEIT') {
                      return {
                        ...g,
                        type: 'seit',
                        activity: g.activity || [''],
                        participation: g.participation || '',
                        outcome: g.outcome || '',
                      };
                    }
                    return { ...g, type: 'setss' };
                  }),
                  date: action.payload.date,
                  startTime: action.payload.startTime,
                  finishTime: action.payload.finishTime,
                  note: action.payload.note,
                  studentId: action.payload.student.id,
                  location: action.payload.location,
                  cancellationReason: action.payload.cancellationReason,
                  cancellationReasonId: action.payload.cancellationReasonId,
                  numberOfStudents: action.payload.numberOfStudents,
                },
        };
      case 'setErrors':
        return {
          ...state,
          errors: action.payload,
        };

      case 'setDate':
        return mutateFormData({ date: action.payload });
      case 'setLocation':
        return mutateFormData({ location: action.payload });
      case 'selectStudent':
        return mutateFormData({ studentId: action.payload });
      case 'clearStudent':
        return {
          ...mutateFormData({
            studentId: undefined,
            date: undefined,
            startTime: undefined,
            finishTime: undefined,
            location: undefined,
            goals: [],
          }),
          errors: undefined,
        };
      case 'setStartTime':
        return mutateFormData({ startTime: action.payload });
      case 'setFinishTime':
        return mutateFormData({ finishTime: action.payload });
      case 'setCancellationReason':
        return mutateFormData({ cancellationReason: action.payload });
      case 'addNewGoal':
        return mutateFormData({
          goals: [
            ...state.formData.goals,
            { ...action.payload, progress: 0, type: 'setss' } as StudentGoalBase,
          ],
        });
      case 'removeGoal':
        return mutateFormData({
          goals: state.formData.goals.filter(
            (goal) => !(goal.type === 'setss' && goal.id === action.payload.id),
          ),
        });
      case 'clearGoals':
        return mutateFormData({
          goals: [],
        });
      case 'setGoalProgress':
        return mutateFormData({
          goals: state.formData.goals.map((goal) =>
            goal.id === action.payload.goalId
              ? { ...goal, progress: action.payload.progress }
              : goal,
          ),
        });
      case 'removeNote':
        return mutateFormData({ note: undefined });
      case 'setNote':
        return mutateFormData({ note: action.payload });
      case 'numberOfStudents':
        return mutateFormData({ numberOfStudents: Number(action.payload) });

      case 'addSeitGoal':
        return mutateFormData({
          goals: [
            ...state.formData.goals,
            { ...action.payload, type: 'seit', activity: [] } as SeitGoal,
          ],
        });
      case 'removeSeitGoal':
        return mutateFormData({
          goals: state.formData.goals.filter((goal, idx) => {
            const currentGoal = state.formData.goals[idx];
            return !(currentGoal.type === 'seit' && idx === action.payload.index);
          }),
        });
      case 'updateSeitGoal': {
        const { index, field, value } = action.payload;
        const updatedGoals = state.formData.goals.map((goal, idx) => {
          if (idx === index && goal.type === 'seit') {
            if (field === 'activity') {
              return {
                ...goal,
                [field]: Array.isArray(value) ? value : value ? [value] : [],
              } as SeitGoal;
            } else {
              return { ...goal, [field]: typeof value === 'string' ? value : '' } as SeitGoal;
            }
          }
          return goal;
        });
        return mutateFormData({ goals: updatedGoals });
      }
      default:
        return state;
    }
  }

  return state;
};

type ReactSessionContextProps = {
  state: FormState;
  dispatch: Dispatch<FormActions>;
  features: { lockOnStudentId?: string };
};
export const RecordSessionContext = createContext<ReactSessionContextProps>({
  state: { isInitialized: false },
  dispatch: () => undefined,
  features: {},
});

type RecordSessionContextProviderProps = { lockOnStudentId?: string; children: ReactNode };

export const RecordSessionContextProvider = ({
  children,
  lockOnStudentId,
}: RecordSessionContextProviderProps) => {
  const [state, dispatch] = useReducer(formStateReducer, {
    isInitialized: false,
  });
  const features = { lockOnStudentId: lockOnStudentId };

  useEffect(() => {
    if (state.isInitialized && lockOnStudentId && state?.formData?.studentId !== lockOnStudentId) {
      dispatch({ type: 'selectStudent', payload: lockOnStudentId });
    }
  }, [state.isInitialized, lockOnStudentId, state?.formData?.studentId]);

  return (
    <RecordSessionContext.Provider value={{ state, dispatch, features }}>
      {children}
    </RecordSessionContext.Provider>
  );
};

RecordSessionContext.displayName = 'RecordSessionContext';
